import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Gift, Plus, Trash2, Edit2, ToggleLeft, ToggleRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { ALL_PLANS } from '../config/plans';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminOfferZone = () => {
  const [offerZones, setOfferZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [newOffer, setNewOffer] = useState({
    plan_id: '',
    content: '',
    video_url: '',
    images: [],
    hyperlinks: [],
    active: true
  });
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newLink, setNewLink] = useState({ text: '', url: '' });

  useEffect(() => {
    fetchOfferZones();
  }, []);

  const fetchOfferZones = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/offer-zones/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        console.log('Fetched offer zones:', response.data.offer_zones);
        setOfferZones(response.data.offer_zones);
      }
    } catch (error) {
      toast.error('Failed to fetch offer zones');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOffer = async () => {
    if (!newOffer.plan_id) {
      toast.error('Please select a plan');
      return;
    }
    if (!newOffer.content.trim()) {
      toast.error('Please enter content');
      return;
    }

    console.log('Creating offer with data:', newOffer);
    console.log('Hyperlinks being sent:', newOffer.hyperlinks);
    alert(`Creating offer with ${newOffer.hyperlinks?.length || 0} hyperlinks: ${JSON.stringify(newOffer.hyperlinks)}`);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/offer-zone/create`,
        newOffer,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Offer zone created successfully');
        setNewOffer({
          plan_id: '',
          content: '',
          video_url: '',
          images: [],
          hyperlinks: [],
          active: true
        });
        setShowCreateForm(false);
        fetchOfferZones();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create offer zone');
    }
  };

  const handleEditOffer = async () => {
    if (!editingOffer.plan_id) {
      toast.error('Please select a plan');
      return;
    }
    if (!editingOffer.content.trim()) {
      toast.error('Please enter content');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/admin/offer-zones/${editingOffer.id}/edit`,
        {
          plan_id: editingOffer.plan_id,
          content: editingOffer.content,
          video_url: editingOffer.video_url,
          images: editingOffer.images,
          hyperlinks: editingOffer.hyperlinks,
          active: editingOffer.active
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Offer zone updated successfully');
        setEditingOffer(null);
        fetchOfferZones();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update offer zone');
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this offer zone?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/offer-zones/${offerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Offer zone deleted successfully');
      fetchOfferZones();
    } catch (error) {
      toast.error('Failed to delete offer zone');
    }
  };

  const handleAddImage = (isEdit = false) => {
    if (!newImageUrl.trim()) {
      toast.error('Please enter image URL');
      return;
    }

    if (isEdit) {
      setEditingOffer({
        ...editingOffer,
        images: [...editingOffer.images, newImageUrl]
      });
    } else {
      setNewOffer({
        ...newOffer,
        images: [...newOffer.images, newImageUrl]
      });
    }
    setNewImageUrl('');
  };

  const handleRemoveImage = (index, isEdit = false) => {
    if (isEdit) {
      setEditingOffer({
        ...editingOffer,
        images: editingOffer.images.filter((_, i) => i !== index)
      });
    } else {
      setNewOffer({
        ...newOffer,
        images: newOffer.images.filter((_, i) => i !== index)
      });
    }
  };

  const handleAddLink = (isEdit = false) => {
    console.log('handleAddLink called, newLink:', newLink);
    
    if (!newLink.text.trim() || !newLink.url.trim()) {
      toast.error('Please enter both link text and URL');
      return;
    }

    const linkToAdd = { text: newLink.text, url: newLink.url };
    console.log('Adding link:', linkToAdd);

    if (isEdit) {
      const updatedLinks = [...(editingOffer.hyperlinks || []), linkToAdd];
      console.log('Updated editing offer hyperlinks:', updatedLinks);
      setEditingOffer({
        ...editingOffer,
        hyperlinks: updatedLinks
      });
    } else {
      const updatedLinks = [...(newOffer.hyperlinks || []), linkToAdd];
      console.log('Updated new offer hyperlinks:', updatedLinks);
      setNewOffer({
        ...newOffer,
        hyperlinks: updatedLinks
      });
    }
    setNewLink({ text: '', url: '' });
    toast.success('Link added');
  };

  const handleRemoveLink = (index, isEdit = false) => {
    if (isEdit) {
      setEditingOffer({
        ...editingOffer,
        hyperlinks: editingOffer.hyperlinks.filter((_, i) => i !== index)
      });
    } else {
      setNewOffer({
        ...newOffer,
        hyperlinks: newOffer.hyperlinks.filter((_, i) => i !== index)
      });
    }
  };

  const renderOfferForm = (offer, isEdit = false) => {
    const data = isEdit ? editingOffer : newOffer;
    const setData = isEdit ? setEditingOffer : setNewOffer;
    const handleSave = isEdit ? handleEditOffer : handleCreateOffer;
    const handleCancel = () => isEdit ? setEditingOffer(null) : setShowCreateForm(false);

    return (
      <div className="bg-gray-50 p-4 rounded-lg mb-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">{isEdit ? 'Edit Offer Zone' : 'Create New Offer Zone'}</h3>
          <button
            className="p-1 hover:bg-gray-200 rounded"
            onClick={handleCancel}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        
        <div className="space-y-4">
          {/* Plan Selection */}
          <div>
            <label className="block text-sm font-medium mb-1">Select Plan *</label>
            <select
              value={data.plan_id}
              onChange={(e) => setData({ ...data, plan_id: e.target.value })}
              className="w-full p-2 border rounded-lg"
            >
              <option value="">Choose a plan...</option>
              {ALL_PLANS.map(plan => (
                <option key={plan.id} value={plan.id}>
                  {plan.icon} {plan.name} - ₹{plan.price}
                </option>
              ))}
            </select>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.active}
              onChange={(e) => setData({ ...data, active: e.target.checked })}
              className="w-4 h-4"
              id="active-toggle"
            />
            <label htmlFor="active-toggle" className="text-sm font-medium cursor-pointer">
              Active (show on checkout)
            </label>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium mb-1">Content (HTML supported) *</label>
            <textarea
              value={data.content}
              onChange={(e) => setData({ ...data, content: e.target.value })}
              className="w-full min-h-[120px] p-3 border rounded-lg"
              placeholder="Enter promotional content... You can use HTML tags like <strong>, <em>, etc."
            />
          </div>

          {/* Video URL */}
          <div>
            <label className="block text-sm font-medium mb-1">YouTube Video URL</label>
            <Input
              value={data.video_url || ''}
              onChange={(e) => setData({ ...data, video_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm font-medium mb-2">Banner Images</label>
            <div className="flex gap-2 mb-2">
              <Input
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                placeholder="Enter image URL"
                className="flex-1"
              />
              <button
                onClick={() => handleAddImage(isEdit)}
                className="px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {data.images && data.images.length > 0 && (
              <div className="space-y-2">
                {data.images.map((img, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-white rounded border">
                    <img src={img} alt={`Banner ${index + 1}`} className="w-16 h-16 object-cover rounded" loading="lazy" decoding="async"/>
                    <span className="flex-1 text-sm truncate">{img}</span>
                    <button
                      onClick={() => handleRemoveImage(index, isEdit)}
                      className="p-1 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Hyperlinks */}
          <div>
            <label className="block text-sm font-medium mb-2">Hyperlinks</label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Input
                value={newLink.text}
                onChange={(e) => setNewLink({ ...newLink, text: e.target.value })}
                placeholder="Link Text"
              />
              <div className="flex gap-2">
                <Input
                  value={newLink.url}
                  onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                  placeholder="https://..."
                  className="flex-1"
                />
                <button
                  onClick={() => handleAddLink(isEdit)}
                  className="px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
            {data.hyperlinks && data.hyperlinks.length > 0 && (
              <div className="space-y-2">
                {data.hyperlinks.map((link, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-white rounded border">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{link.text}</div>
                      <div className="text-xs text-gray-500 truncate">{link.url}</div>
                    </div>
                    <button
                      onClick={() => handleRemoveLink(index, isEdit)}
                      className="p-1 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={handleSave}
            className="w-full py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            {isEdit ? 'Update Offer' : 'Create an Offer'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Offer Zone Management
          </CardTitle>
          {!showCreateForm && !editingOffer && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create Offer
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Create Form */}
        {showCreateForm && renderOfferForm(null, false)}

        {/* Edit Form */}
        {editingOffer && renderOfferForm(editingOffer, true)}

        {/* Offers List */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : offerZones.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No offer zones created yet</p>
        ) : (
          <div className="space-y-3">
            {offerZones.map((offer) => {
              const plan = ALL_PLANS.find(p => p.id === offer.plan_id);
              return (
                <div
                  key={offer.id}
                  className={`border rounded-lg p-4 ${
                    offer.active ? 'bg-white' : 'bg-gray-100'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-lg font-semibold">
                          {plan ? `${plan.icon} ${plan.name}` : offer.plan_id}
                        </span>
                        {!offer.active && (
                          <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded text-xs">
                            Inactive
                          </span>
                        )}
                      </div>

                      <div className="text-sm text-gray-600 space-y-1">
                        <div 
                          className="line-clamp-2" 
                          dangerouslySetInnerHTML={{ __html: offer.content }}
                        />
                        {offer.video_url && (
                          <p>📹 Video included</p>
                        )}
                        {offer.images && offer.images.length > 0 && (
                          <p>🖼️ {offer.images.length} image(s)</p>
                        )}
                        {offer.hyperlinks && offer.hyperlinks.length > 0 && (
                          <p>🔗 {offer.hyperlinks.length} link(s)</p>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        className="p-2 border border-gray-300 hover:bg-gray-100 rounded"
                        onClick={() => {
                          console.log('Editing offer:', offer);
                          setEditingOffer({
                            ...offer,
                            images: offer.images || [],
                            hyperlinks: offer.hyperlinks || []
                          });
                        }}
                        title="Edit offer"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        className="p-2 bg-red-600 hover:bg-red-700 text-white rounded"
                        onClick={() => handleDeleteOffer(offer.id)}
                        title="Delete offer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminOfferZone;
